import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { TablePagination } from "@mui/material";
import { useDebounce, useQueryParams } from "../../../hooks";
import { getAllPlans } from "./Helper";
import InvoiceForm from "../../../components/InvoiceForm";

const CustomPlans = () => {
    const context = useOutletContext();
    const [plans, setPlans] = useState([]);
    const { getParam, setMultiParam } = useQueryParams();
    const currentPage = getParam("page");
    const currentLimit = getParam("pageSize");
    const [usersPage, setUsersPage] = useState(parseInt(currentPage) || 0);
    const [usersLimit, setUsersLimit] = useState(parseInt(currentLimit) || 10);
    const [usersTotal, setUsersTotal] = useState(0);
    const [search] = useState('');
    const [status, setStatus] = useState('active');
    const debounceSearch = useDebounce(search, 1000);
    const [showInvoiceForm, setShowInvoiceForm] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);

    const handleChangePage = (event, newPage) => {
        fetchData(newPage, usersLimit, status);
        setUsersPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setUsersLimit(parseInt(event.target.value, 10));
        setUsersPage(0);
        fetchData(0, parseInt(event.target.value, 10), status);
    };

    const handleStatusChange = (newStatus) => {
        setStatus(newStatus);
        setUsersPage(0);
        fetchData(0, usersLimit, newStatus);
    };

    const handleCreateInvoice = () => {
        setSelectedPlan(null);
        setShowInvoiceForm(true);
    };

    const fetchData = (page, limit, currentStatus) => {
        const hasPurchase = currentStatus === 'active';
        getAllPlans(page, limit, debounceSearch, hasPurchase)
            .then((response) => {
                setPlans(response?.data?.result)
                setUsersTotal(response?.data?.meta?.total * response?.data?.meta?.pages || 0);
            })
            .catch((err) => console.log(err));
    };

    useEffect(() => {
        context(["User Manager", "Custom Plans"]);
        fetchData(usersPage, usersLimit, status);
    }, [debounceSearch, status]);

    useEffect(() => {
        setMultiParam({ "page": usersPage, "pageSize": usersLimit });
    }, [usersPage, usersLimit]);

    return (
        <div className="p-5 shadow-md bg-white">
            <div className="flex flex-row justify-between gap-2">
                <h3
                    className="col-span-3 w-full text-lg uppercase tracking-wider font-bold"
                    style={{ color: "#2EBAA3" }}
                >
                    Custom Plans
                </h3>
                <div className="w-full flex items-center justify-end gap-2">
                    <div className="inline-flex rounded-md shadow-sm" role="group">
                        <button
                            type="button"
                            onClick={() => handleStatusChange('active')}
                            className={`px-4 py-2 text-sm font-medium rounded-l-lg ${status === 'active'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                                }`}
                        >
                            Paid
                        </button>
                        <button
                            type="button"
                            onClick={() => handleStatusChange('inactive')}
                            className={`px-4 py-2 text-sm font-medium rounded-r-lg ${status === 'inactive'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                                }`}
                        >
                            Unpaid
                        </button>
                    </div>
                </div>
            </div>
            <br />
            <div className="overflow-x-auto relative w-full">
                <table className="w-full text-sm text-left text-gray-500">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                            <th scope="col" className="py-3 px-6 uppercase w-1/10">Sr.</th>
                            <th scope="col" className="py-3 px-6 uppercase w-1/10">Name</th>
                            <th scope="col" className="py-3 px-6 uppercase w-1/10">Company Name</th>
                            <th scope="col" className="py-3 px-6 uppercase w-1/10">Amount</th>
                            <th scope="col" className="py-3 px-6 uppercase w-1/10">Final Amount</th>
                            <th scope="col" className="py-3 px-6 uppercase w-1/10">Gst Amount</th>
                            <th scope="col" className="py-3 px-6 uppercase w-1/10">Usage</th>
                            <th scope="col" className="py-3 px-8 uppercase w-1/10">Plan Id</th>
                            <th scope="col" className="py-3 px-6 uppercase w-1/10">Payment Provider</th>
                            <th scope="col" className="py-3 px-6 uppercase w-1/10">Interval</th>
                            <th scope="col" className="py-3 px-8 uppercase w-1/12">
                                {status === 'active' ? 'Purchased at' : 'Created at'}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {plans?.map((user, index) => {
                            return (
                                <tr className="bg-white border-b" key={user.id}>
                                    <th
                                        scope="row"
                                        className="py-4 px-6 font-medium text-gray-900 whitespace-nowrap w-1/10"
                                    >
                                        {index + 1}
                                    </th>
                                    <td className="py-4 px-6 w-1/10">
                                        {`${(user?.user?.firstName || '') + " " + (user?.user?.lastName || '')}`.trim()}
                                    </td>
                                    <td className="py-4 px-6 w-1/10">
                                        {user?.user?.company ? user?.user?.company : ' '}
                                    </td>
                                    <td className="py-4 px-6 w-1/10">
                                        {user.payment_provider?.toLowerCase() === 'stripe' ? '$' : '₹'}{user.display_amount}
                                    </td>
                                    <td className="py-4 px-6 w-1/10">
                                        {user.payment_provider?.toLowerCase() === 'stripe' ? '$' : '₹'}{user.amount ? user.amount : ' '}
                                    </td>
                                    <td className="py-4 px-6 w-1/10">
                                        {user.gstAmount ? user.payment_provider?.toLowerCase() === 'stripe' ? '$' : '₹' : ' '}{user.gstAmount ? user.gstAmount : ' '}
                                    </td>
                                    <td className="py-4 px-3 w-1/10">
                                        {user.usage && Object.keys(user.usage).length > 0 ? (
                                            <select className="border rounded px-2 py-1 w-full">
                                                {Object.entries(user.usage).map(([key, value]) => (
                                                    <option
                                                        key={key}
                                                        value={value === null || value === undefined ? '' : (typeof value === 'object' ? JSON.stringify(value) : value)}
                                                    >
                                                        {key}: {
                                                            value === null || value === undefined ? 'N/A' :
                                                                (typeof value === 'object' && value.type !== undefined) ? value.type :
                                                                    (typeof value === 'object') ? 'Object' :
                                                                        (typeof value === 'boolean') ? (value ? 'Yes' : 'No') :
                                                                            value
                                                        }
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <span>N/A</span>
                                        )}
                                    </td>
                                    <td className="py-4 px-6 break-words w-1/10" title={user?.PlanId}>
                                        {user?.PlanId}
                                    </td>
                                    <td className="py-4 px-6 w-1/10">
                                        {user?.payment_provider}
                                    </td>
                                    <td className="py-4 px-6 w-1/10">
                                        {user?.interval?.toUpperCase()}
                                    </td>
                                    {status === 'active' ?
                                    <td className="py-4 px-1">
                                    {user?.purchased_at
                                        ? new Date(user.purchased_at).toLocaleString("en-GB", {
                                            year: "numeric",
                                            month: "2-digit",
                                            day: "2-digit",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            hour12: true,
                                            }).replace(",", "")
                                            .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$2-$1")
                                            .replace(" ", ", ")
                                    : ""}
                                    </td>:<td className="py-4 px-1">
                                    {user?.created_at
                                        ? new Date(user.created_at).toLocaleString("en-GB", {
                                            year: "numeric",
                                            month: "2-digit",
                                            day: "2-digit",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            hour12: true,
                                            }).replace(",", "")
                                            .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$2-$1")
                                            .replace(" ", ", ")
                                    : ""}
                                    </td>}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <TablePagination
                component="div"
                count={usersTotal}
                page={usersPage}
                onPageChange={handleChangePage}
                rowsPerPage={usersLimit}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[2, 5, 10, 25, 50, 100]}
            />

            <InvoiceForm
                isOpen={showInvoiceForm}
                onClose={() => setShowInvoiceForm(false)}
                selectedPlan={selectedPlan}
            />
        </div>
    )
}

export default CustomPlans
